/**
 * Who stands on which side, derived from one payload rather than two.
 *
 * `FightStateDTO.participants` already carries every name and its `side_id`,
 * and the side summary sends only `lead_character_id` — so membership is a
 * grouping, never a second list that could disagree with the first. Shared by
 * `FightEstablishmentStage.vue` and `SideSelectionDonut.vue` so the GM's
 * balance panel and the player's establishment screen never read the same
 * fight differently.
 *
 * Pure: no Vue, no i18n. The one string it cannot derive — what to call a
 * lead who is not in the participant list — is passed in.
 */

export function groupParticipantsBySide(participants) {
  const bySide = new Map();
  (participants || []).forEach(participant => {
    if (!participant || !participant.side_id) return;
    if (!bySide.has(participant.side_id)) bySide.set(participant.side_id, []);
    bySide.get(participant.side_id).push(participant);
  });
  return bySide;
}

/**
 * One card per arc: the side, its lead, the rest of its members, and whether
 * the viewer is on it. A side that records a `lead_character_id` nobody in
 * the participant list matches still shows a lead — it is a fact about the
 * side, and hiding it would read as "no lead" which is a different fact.
 */
export function buildSideCards(arcs, participants, options = {}) {
  const {selfCharacterId = null, unknownLeadName = ''} = options;
  const bySide = groupParticipantsBySide(participants);

  return (arcs || []).map(arc => {
    const members = bySide.get(arc.id) || [];
    const leadId = arc.side.lead_character_id || null;
    const lead = members.find(m => m.character_id === leadId)
        || (leadId ? {character_id: leadId, name: unknownLeadName} : null);

    return {
      id: arc.id,
      side: arc.side,
      lead,
      members,
      others: members.filter(m => !lead || m.character_id !== lead.character_id),
      isMine: !!selfCharacterId && members.some(m => m.character_id === selfCharacterId),
    };
  });
}
